import { useDataProvider, useNotify } from 'react-admin';
import { useEffect, useState } from "react";
import InputFieldCustom from '../Commons/InputFieldCustom'
import { useMediaQuery } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import BreadCrumbCustom from '../Commons/BreadCrum';
import historyTracker from '../Commons/historyTracker';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import AuthNotification from '../Commons/AuthNotification';
import { Style } from '../Commons/Styles.jsx';
import BottomToolBar from '../Commons/BottomToolBar';
import InternalMenu from '../Commons/InternalMenu';
// TODO: reemplazar por el ícono definitivo de Proveedores cuando esté disponible
import providers from '../Commons/Icons/apps.svg';
import providerList from '../Commons/Icons/book.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

const formatDate = (value) => value ? new Date(value).toLocaleDateString('es-GT') : '';

const formatMoney = (value) =>
    value === null || value === undefined
        ? ''
        : parseFloat(value).toLocaleString('es-GT', { style: 'decimal', minimumFractionDigits: 2 });

// Detalle de UNA factura de proveedor (sp_devuelve_factura). Solo lectura: las facturas
// ya estan contabilizadas, esta pantalla es unicamente para consultar la informacion, no
// para editarla. Se llega aqui haciendo click en una fila de ProviderInvoicesList.
const ProviderInvoiceShow = () => {
    const { id } = useParams();
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const [openAuth, setOpenAuth] = useState(false);
    const [auth, setAuth] = useState(false);
    // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
    // "initialLoading" en el return): sin este estado la ventana se veia en blanco
    // (solo el breadcrumb) mientras se traian los permisos y el detalle de la factura.
    const [initialLoading, setInitialLoading] = useState(true);
    const [authAccountingExpenses, setAuthAccountingExpenses] = useState(false);
    const [authInventoryIntegration, setAuthInventoryIntegration] = useState(false);
    const [providerCode, setProviderCode] = useState('');
    const [providerName, setProviderName] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [series, setSeries] = useState('');
    const [invoiceDate, setInvoiceDate] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [registrationDate, setRegistrationDate] = useState('');
    const [currencyName, setCurrencyName] = useState('');
    const [invoiceAmount, setInvoiceAmount] = useState('');
    const [paidAmount, setPaidAmount] = useState('');
    const [balance, setBalance] = useState('');
    const [pendingToRegister, setPendingToRegister] = useState('');
    // Valor numerico crudo del saldo por registrar (pendingToRegister de arriba ya viene
    // formateado para mostrar) y el codigo crudo de moneda ('ML'/'ME', currencyType ya
    // viene traducido a texto para mostrar): ambos hacen falta para decidir si el boton
    // "Registrar" debe aparecer y si pedir tipo de cambio.
    const [pendingToRegisterValue, setPendingToRegisterValue] = useState(null);
    const [currencyCode, setCurrencyCode] = useState('');
    // fecha_registro en cbfactura es NULL hasta que la factura se registra. isRegistered
    // por si solo NO alcanza para decidir si el campo se vuelve editable (ver JSX): solo
    // debe poder capturarse cuando ya se puede registrar de verdad (canRegister, mas abajo
    // -- saldo por registrar en 0), si no queda de solo lectura aunque todavia no este
    // registrada.
    const [isRegistered, setIsRegistered] = useState(false);
    const [registrationDateInput, setRegistrationDateInput] = useState('');
    // Tipo de cambio a aplicar al registrar: solo aplica (y solo se pide) para facturas en
    // moneda extranjera -- sp_registra_facturas fuerza tipo_cambio=1 para moneda local sin
    // importar lo que se le mande. Reutiliza el campo "Tasa de Referencia" en vez de agregar
    // uno nuevo porque es literalmente lo que esa columna termina valiendo al registrar.
    const [exchangeRateInput, setExchangeRateInput] = useState('');
    const [registering, setRegistering] = useState(false);
    const [referenceRate, setReferenceRate] = useState('');
    const [ducaReference, setDucaReference] = useState('');
    const [ducaNumber, setDucaNumber] = useState('');
    const [importDescription, setImportDescription] = useState('');
    const [daiAmount, setDaiAmount] = useState('');

    // Extraido del useEffect (en vez de dejarlo como IIFE inline) para poder reutilizarlo
    // despues de registrar la factura: hay que releer todo (fecha_registro, saldo por
    // registrar, etc.) para que el boton "Registrar" desaparezca y el resto de campos
    // reflejen el nuevo estado, sin duplicar toda esta logica de carga.
    const loadInvoice = async () => {
        try {
            const authdata = { data: hasPermissions('ProviderInvoice', 'read') };
            if (authdata.data.length > 0) {
                setAuth(authdata.data[0]);
                setOpenAuth(!authdata.data[0]);

                if (authdata.data[0]) {
                    // Permiso independiente para el boton "Gastos Contables": si el usuario
                    // no lo tiene, el boton simplemente no se muestra (no bloquea el resto
                    // de la vista, a diferencia del permiso de lectura de la factura).
                    const accountingExpensesData = hasPermissions('AccountingExpense', 'list');
                    setAuthAccountingExpenses(accountingExpensesData.length > 0 && accountingExpensesData[0]);

                    // Mismo patron que el permiso de Gastos Contables arriba, permiso
                    // independiente para el boton "Integración de Inventario".
                    const inventoryIntegrationData = hasPermissions('InventoryIntegration', 'list');
                    setAuthInventoryIntegration(inventoryIntegrationData.length > 0 && inventoryIntegrationData[0]);

                    const { data } = await dataProvider.getOne('provider/invoice', { id });

                    historyTracker.addPath(location.pathname, data.invoiceNumber ? `Factura ${data.invoiceNumber}` : 'Factura', 5);

                    setProviderCode(data.providerCode?.toString());
                    setProviderName(data.providerName);
                    setInvoiceNumber(data.invoiceNumber);
                    setSeries(data.series);
                    setInvoiceDate(formatDate(data.invoiceDate));
                    setPaymentDate(formatDate(data.paymentDate));
                    setRegistrationDate(formatDate(data.registrationDate));
                    setCurrencyName(data.currencyName ? `${data.currencyName}${data.currencySymbol ? ` (${data.currencySymbol})` : ''}` : '');
                    setCurrencyCode(data.currencyType);
                    setIsRegistered(!!data.registrationDate);
                    // Se limpian los inputs de registro en cada carga (tambien tras registrar
                    // con exito, ver handleRegisterInvoice): si la factura ya quedo registrada
                    // el campo vuelve a quedar de solo lectura (ver JSX) y no hace falta
                    // conservar lo que se haya tecleado.
                    setRegistrationDateInput('');
                    setExchangeRateInput('');
                    setInvoiceAmount(formatMoney(data.invoiceAmount));
                    setPaidAmount(formatMoney(data.paidAmount));
                    setBalance(formatMoney(data.balance));

                    // "Saldo por Registrar" = lo que todavia falta clasificar en Gastos
                    // Contables / Integración de Inventario, NO el saldo de pago (balance,
                    // arriba). Mismo calculo que ya usan AccountingExpenseCreate.jsx e
                    // InventoryIntegrationCreate.jsx para validar el monto maximo permitido al
                    // registrar un gasto/integracion nuevo: las dos sumas (gastos contables +
                    // integraciones de inventario ya registradas) se combinan porque ambas
                    // restan del mismo monto de la factura (montointegraciongasto y
                    // montointegracioninventarios son columnas separadas en cbfactura, pero las
                    // dos descuentan del mismo total). Promise.allSettled para que, si un
                    // catalogo falla, no tumbe el resto de la pantalla.
                    const [expensesResult, integrationsResult] = await Promise.allSettled([
                        dataProvider.getList('provider/accountingexpense', { filter: { invoice: id }, pagination: {}, sort: {} }),
                        dataProvider.getList('provider/inventoryintegration', { filter: { invoice: id }, pagination: {}, sort: {} }),
                    ]);
                    var registeredExpenses = 0;
                    var registeredIntegrations = 0;
                    if (expensesResult.status === 'fulfilled') {
                        registeredExpenses = expensesResult.value.data.reduce((total, item) => total + parseFloat(item.amount || 0), 0);
                    } else {
                        console.error('No se pudo cargar los gastos contables ya registrados de la factura:', expensesResult.reason);
                    }
                    if (integrationsResult.status === 'fulfilled') {
                        registeredIntegrations = integrationsResult.value.data.reduce((total, item) => total + parseFloat(item.amount || 0), 0);
                    } else {
                        console.error('No se pudo cargar las integraciones de inventario ya registradas de la factura:', integrationsResult.reason);
                    }
                    if (expensesResult.status === 'fulfilled' || integrationsResult.status === 'fulfilled') {
                        var pending = parseFloat(data.invoiceAmount) - (registeredExpenses + registeredIntegrations);
                        setPendingToRegister(formatMoney(pending));
                        setPendingToRegisterValue(pending);
                    } else {
                        setPendingToRegister('');
                        setPendingToRegisterValue(null);
                    }
                    setReferenceRate(data.referenceRate?.toString());
                    setDucaReference(data.ducaReference);
                    setDucaNumber(data.ducaNumber);
                    setImportDescription(data.importDescription);
                    setDaiAmount(data.importCorrelative ? formatMoney(data.daiAmount) : '');
                }
            } else {
                setAuth(false);
                setOpenAuth(true);
            }
        }
        catch (error) {
            console.log(error);
        }
        finally {
            setInitialLoading(false);
        }
    };

    useEffect(() => {
        loadInvoice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataProvider, location.pathname, id]);

    const handleRedirectProviderList = () => {
        navigate(`/provider`);
    };

    const handleRedirectInvoices = () => {
        navigate({
            pathname: '/provider/invoice',
            search: `?filter=${encodeURIComponent(JSON.stringify({ provider: providerCode }))}&providerName=${encodeURIComponent(providerName || '')}`,
        }, { state: { providerName, providerCode } });
    };

    const handleOpenAccountingExpenses = () => {
        navigate({
            pathname: '/provider/accountingexpense',
            search: `?filter=${encodeURIComponent(JSON.stringify({ invoice: id }))}&invoiceNumber=${encodeURIComponent(invoiceNumber || '')}`,
        }, { state: { invoiceNumber, invoiceId: id } });
    };

    const handleOpenInventoryIntegration = () => {
        navigate({
            pathname: '/provider/inventoryintegration',
            search: `?filter=${encodeURIComponent(JSON.stringify({ invoice: id }))}&invoiceNumber=${encodeURIComponent(invoiceNumber || '')}`,
        }, { state: { invoiceNumber, invoiceId: id } });
    };

    // El boton "Registrar" solo debe verse cuando ya no falta nada por clasificar (mismo
    // chequeo que hace sp_registra_facturas: gastos contables + integracion de inventario +
    // activos fijos debe sumar exactamente el monto de la factura) y la factura todavia no
    // esta registrada. Se usa una tolerancia de centavos por redondeo, igual que
    // AccountingExpenseCreate.jsx/InventoryIntegrationCreate.jsx al validar contra el saldo.
    const canRegister = !isRegistered && pendingToRegisterValue !== null && Math.abs(pendingToRegisterValue) < 0.005;

    // "Gastos Contables"/"Int. Inventario" solo tienen sentido mientras todavia queda saldo
    // por clasificar: una vez el Saldo por Registrar llega a 0 (misma tolerancia que
    // canRegister, arriba) ya no hay contra que monto cargar un gasto/integracion nuevo --
    // AccountingExpenseCreate.jsx/InventoryIntegrationCreate.jsx de hecho rechazarian el
    // alta por excederse del saldo. pendingToRegisterValue arranca en null (todavia no
    // cargo la factura) y en ese caso tampoco se muestran, para no parpadear el boton antes
    // de tener el dato real.
    const hasPendingToRegister = pendingToRegisterValue !== null && Math.abs(pendingToRegisterValue) >= 0.005;

    const handleRegisterInvoice = async () => {
        // sp_registra_facturas trata fecha_registro como opcional (si se manda vacia, la
        // calcula sola con la fecha de factura o de ingreso), pero en esta pantalla se pide
        // obligatoria para que quede explicito con que fecha se esta contabilizando la
        // factura, en vez de depender del calculo automatico del SP.
        if (!registrationDateInput) {
            notify('Debe indicar la fecha de registro.', { type: 'warning' });
            return;
        }

        // Tipo de cambio: sp_registra_facturas lo ignora y lo fuerza a 1 para moneda local,
        // pero para moneda extranjera exige uno valido (> 0) -- no hay hoy ninguna otra
        // pantalla que ya lo capture, asi que se pide aca mismo reutilizando el campo "Tasa
        // de Referencia" (ver JSX), que es literalmente lo que esa columna termina valiendo
        // al registrar.
        if (currencyCode === 'ME' && (!exchangeRateInput || isNaN(parseFloat(exchangeRateInput)) || parseFloat(exchangeRateInput) <= 0)) {
            notify('Debe indicar un tipo de cambio válido para registrar una factura en moneda extranjera.', { type: 'warning' });
            return;
        }

        setRegistering(true);
        try {
            await dataProvider.create(`provider/invoice/${id}/register`, {
                data: {
                    registrationDate: registrationDateInput,
                    exchangeRate: currencyCode === 'ME' ? exchangeRateInput : null,
                },
            });
            notify('Factura registrada correctamente.', { type: 'success' });
            // Una vez registrada, la factura sale de sp_lista_cbfactura_editable (que solo
            // lista estado='N', ver Get_Provider_Invoices/Providersapi.cs) y ya no pertenece
            // a esta pantalla -- se vuelve al listado de Facturas por Procesar del mismo
            // proveedor en vez de recargar aca. Quedarse aca dejaba visibles "Gastos
            // Contables"/"Int. Inventario" (que no estan condicionados a isRegistered) sobre
            // una factura que ya no deberia poder seguir clasificandose: una vez registrada,
            // esos montos ya quedaron fijados en la partida contable del registro.
            handleRedirectInvoices();
        } catch (error) {
            notify(error?.message || 'No se pudo registrar la factura.', { type: 'error' });
        } finally {
            setRegistering(false);
        }
    };

    const [openProviders, setOpenProviders] = useState(true);

    const handleClickOpenProviders = () => {
        setOpenProviders(!openProviders);
    };

    const menus = [{
        icon: <img src={providers} alt="logo" style={Style.MenuLogo} />, name: 'Proveedores',
        open: openProviders,
        openAction: handleClickOpenProviders,
        actions: [
            { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Listado', onClick: handleRedirectProviderList },
        ]
    }];

    const hasImportInfo = !!(ducaReference || ducaNumber || importDescription || daiAmount);

    return (
        <div class="container">
            <BreadCrumbCustom history={historyTracker.getHistory()} />
            {initialLoading ? (
                // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y
                // el mismo CircularProgress que ya se usa para el spinner de registro (mas
                // abajo, controlado por "registering"), pero aqui cubre la espera de auth +
                // detalle de la factura, que antes dejaba la pantalla en blanco.
                <div class="Loading" style={{ display: 'block' }}>
                    <CircularProgress size="15rem" />
                </div>
            ) : auth ? (
                <div class="edit">
                    {!isSmall ? (
                        // Mismo patron de "Loading" que AccountingExpenseCreate.jsx/
                        // InventoryIntegrationCreate.jsx: mientras se registra, se muestra el
                        // spinner y se atenua el resto del formulario (disabled en BottomToolBar,
                        // mas abajo, ya bloquea los botones).
                        <div class="Loading" style={{ display: registering ? 'block' : 'none' }}>
                            <CircularProgress size="15rem" />
                        </div>
                    ) : null}
                    {!isSmall ? (
                        <div class="container" style={{ opacity: registering ? 0.3 : 1 }}>
                            <div class="row">
                                <InputFieldCustom readOnly value={providerName} setValue={setProviderName} col="col-3" width="95%" label="Proveedor" type="text" />
                                <InputFieldCustom readOnly value={invoiceNumber} setValue={setInvoiceNumber} col="col-3" width="95%" label="Número" type="text" />
                                <InputFieldCustom readOnly value={series} setValue={setSeries} col="col-3" width="95%" label="Serie" type="text" />
                                <InputFieldCustom readOnly value={invoiceDate} setValue={setInvoiceDate} col="col-3" width="95%" label="Fecha de Factura" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={paymentDate} setValue={setPaymentDate} col="col-3" width="95%" label="Fecha de Pago" type="text" />
                                {canRegister ? (
                                    // Solo se vuelve editable cuando ya se puede registrar (saldo por
                                    // registrar en 0): antes de eso no tiene caso pedirla, y una vez
                                    // registrada queda fija. Obligatoria (ver validacion en
                                    // handleRegisterInvoice) aunque el propio SP la tome como opcional.
                                    <InputFieldCustom value={registrationDateInput} setValue={setRegistrationDateInput} col="col-3" width="95%" label="Fecha de Registro *" type="date" date />
                                ) : (
                                    <InputFieldCustom readOnly value={registrationDate} setValue={setRegistrationDate} col="col-3" width="95%" label="Fecha de Registro" type="text" />
                                )}
                                <InputFieldCustom readOnly value={currencyName} setValue={setCurrencyName} col="col-3" width="95%" label="Moneda" type="text" />
                                {(!isRegistered && currencyCode === 'ME') ? (
                                    // Solo se pide capturar el tipo de cambio para moneda extranjera:
                                    // en moneda local sp_registra_facturas lo ignora y lo fuerza a 1.
                                    <InputFieldCustom value={exchangeRateInput} setValue={setExchangeRateInput} col="col-3" width="95%" label="Tasa de Referencia *" type="text" />
                                ) : (
                                    <InputFieldCustom readOnly value={referenceRate} setValue={setReferenceRate} col="col-3" width="95%" label="Tasa de Referencia" type="text" />
                                )}
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={invoiceAmount} setValue={setInvoiceAmount} col="col-3" width="95%" label="Monto (Q)" type="text" />
                                <InputFieldCustom readOnly value={paidAmount} setValue={setPaidAmount} col="col-3" width="95%" label="Abonado (Q)" type="text" />
                                <InputFieldCustom readOnly value={balance} setValue={setBalance} col="col-3" width="95%" label="Saldo (Q)" type="text" />
                                <InputFieldCustom readOnly value={pendingToRegister} setValue={setPendingToRegister} col="col-3" width="95%" label="Saldo por Registrar (Q)" type="text" />
                            </div>
                            {hasImportInfo ? (
                                <>
                                    <div class="row" style={{ paddingTop: '1.2em' }}>
                                        <div class="col-4">
                                            <label style={Style.Label}>Importación</label>
                                        </div>
                                    </div>
                                    <div class="row">
                                        <InputFieldCustom readOnly value={ducaReference} setValue={setDucaReference} col="col-4" width="95%" label="Referencia DUCA" type="text" />
                                        <InputFieldCustom readOnly value={ducaNumber} setValue={setDucaNumber} col="col-4" width="95%" label="No. DUCA" type="text" />
                                        <InputFieldCustom readOnly value={daiAmount} setValue={setDaiAmount} col="col-4" width="95%" label="Monto DAI (Q)" type="text" />
                                    </div>
                                    <div class="row">
                                        {/* referenceCol="col-4": alinea con la fila de arriba (Referencia
                                            DUCA/No. DUCA/Monto DAI), que son 3 campos col-4 -- ver el
                                            comentario de computeTextareaWidth en InputFieldCustom.jsx. */}
                                        <InputFieldCustom readOnly value={importDescription} setValue={setImportDescription} col="col-12" width="95%" label="Descripción" type="text" component="textarea" referenceCol="col-4" />
                                    </div>
                                </>
                            ) : null}
                            <div style={{ marginTop: '2em' }}>
                                {/* "Registrar" reutiliza el permiso "ProviderInvoice" (el mismo que ya
                                    exige el resto de la pantalla) en vez de pedir uno nuevo en Auth0
                                    -- mismo criterio que ya se uso para los permisos de Gastos
                                    Contables/Int. Inventario en esta misma pantalla. */}
                                <BottomToolBar disabled={registering} buttons={[{ auth: authAccountingExpenses && hasPendingToRegister, action: handleOpenAccountingExpenses, name: 'Gastos Contables' }, { auth: authInventoryIntegration && hasPendingToRegister, action: handleOpenInventoryIntegration, name: 'Int. Inventario' }, { auth: auth && canRegister, action: handleRegisterInvoice, name: registering ? 'Registrando...' : 'Registrar' }]} menu={<InternalMenu menus={menus} />} />
                            </div>
                        </div>
                    ) : (
                        <div class="container">
                        </div>
                    )}
                </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)
            }
        </div>
    )
}

export default ProviderInvoiceShow;
